import { useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SUBSCRIBER_NOTIFICATION_TYPE } from 'apps/fleet-planner/constants/loads'
import {
  updateLoad as updateLoadAction,
  updateLoadTruckSuccess,
  updateLoadTrailerSuccess,
  updateLoadDispatcherSuccess,
  updateLoadDriversSuccess,
} from 'apps/fleet-planner/utils/actions/loadDetails'
import * as Api from 'apps/fleet-planner/utils/api'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors/load'
import { ILoad, ICustomerContact } from '../types'

interface IUseShipmentApiProps {
  /**
   * Send updates to Redux when successfully executing async
   * methods in this component
   * @default true
   */
  updateRedux?: boolean
}

interface IUseShipmentApiState {
  error?: string
  isLoading?: boolean
  resetError: () => void
  updateShipmentDispatcher: (dispatcherId: string | null) => Promise<ILoad | undefined>
  updateShipmentDrivers: (driverIds: string[]) => Promise<ILoad | undefined>
  updateShipmentSubscribers: (
    subscribersList: ICustomerContact[],
    notificationOption: SUBSCRIBER_NOTIFICATION_TYPE
  ) => Promise<ILoad | undefined>
  updateShipmentTruck: (truckId: string | null) => Promise<ILoad | undefined>
  updateShipmentTrailer: (trailerId: string | null) => Promise<ILoad | undefined>
  updateShipment: (changes: Partial<ILoad>) => void
}

const defaultProps: IUseShipmentApiProps = {
  updateRedux: true,
}

/**
 * Hook used to update shipments and store state
 */
export const useShipmentApi = ({
  updateRedux,
}: IUseShipmentApiProps = defaultProps): IUseShipmentApiState => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  // Current load
  const { load: currentLoad } = useSelector(loadDetailsSelector)
  const dispatch = useDispatch()

  const resetError = useCallback(() => setError(''), [])

  /**
   * Interface to update load via Redux. Triggers PUT call to /shipments
   * @note This should not be used for disptchers, drivers, trailer, or trucks
   */
  const updateShipment = (changes: Partial<ILoad>) => {
    dispatch(
      updateLoadAction({
        ...currentLoad,
        ...changes,
      })
    )
  }

  /**
   * Updates load.dispatcher
   */
  const updateShipmentDispatcher = useCallback(
    async (dispatcherId: string | null) => {
      setLoading(true)
      try {
        const { shipment } = await Api.updateShipmentDispatcher(currentLoad.id!, dispatcherId)
        setLoading(false)
        setError('')

        // Update redux
        dispatch(updateLoadDispatcherSuccess(shipment))

        return shipment
      } catch (e) {
        setLoading(false)
        setError(e)
        return undefined
      }
    },
    [currentLoad.id, dispatch]
  )

  /**
   * Updates load.drivers
   */
  const updateShipmentDrivers = useCallback(
    async (driverIds: string[]) => {
      setLoading(true)
      try {
        const { shipment } = await Api.updateShipmentDrivers(currentLoad.id!, driverIds)
        setLoading(false)
        setError('')

        // Update redux
        dispatch(updateLoadDriversSuccess(shipment))

        return shipment
      } catch (e) {
        setLoading(false)
        setError(e)
        return undefined
      }
    },
    [currentLoad.id, dispatch]
  )

  /**
   * Updates load subscribers
   */
  const updateShipmentSubscribers = useCallback(
    async (
      subscribersList: ICustomerContact[],
      notificationOption: SUBSCRIBER_NOTIFICATION_TYPE
    ) => {
      setLoading(true)
      try {
        const { shipment } = await Api.updateShipmentSubscribers(
          currentLoad.id!,
          subscribersList,
          notificationOption
        )
        setLoading(false)
        setError('')

        // Update redux
        dispatch(updateLoadDriversSuccess(shipment))

        return shipment
      } catch (e) {
        setLoading(false)
        setError(e)
        return undefined
      }
    },
    [currentLoad.id, dispatch]
  )

  /**
   * Updates load.truck
   */
  const updateShipmentTruck = useCallback(
    async (truckId: string | null) => {
      setLoading(true)
      try {
        const { shipment } = await Api.updateShipmentTruck(currentLoad.id!, truckId)
        setLoading(false)
        setError('')

        // Update redux
        dispatch(updateLoadTruckSuccess(shipment))

        return shipment
      } catch (e) {
        setLoading(false)
        setError(e)
        return undefined
      }
    },
    [currentLoad.id, dispatch]
  )

  /**
   * Updates load.trailer
   */
  const updateShipmentTrailer = useCallback(
    async (trailerId: string | null) => {
      setLoading(true)
      try {
        const { shipment } = await Api.updateShipmentTrailer(currentLoad.id!, trailerId)
        setLoading(false)
        setError('')

        // Update redux
        if (updateRedux) {
          dispatch(updateLoadTrailerSuccess(shipment))
        }

        return shipment
      } catch (e) {
        setLoading(false)
        setError(e)
        return undefined
      }
    },
    [currentLoad.id, dispatch, updateRedux]
  )

  return {
    error,
    isLoading,
    resetError,
    updateShipmentDispatcher,
    updateShipmentDrivers,
    updateShipmentSubscribers,
    updateShipmentTruck,
    updateShipmentTrailer,
    updateShipment,
  }
}
